import React, { useEffect, useState } from "react";

const STEPS = [
  "Reading your invoices",
  "Extracting your purchases",
  "Categorising your items",
  "Building your intelligence report",
  "Crafting your weekly list",
  "Almost ready",
];

/**
 * Animated piggy-bank + falling coins overlay shown during PDF analysis.
 * Pure CSS — no external libs. Feels celebratory: "we're saving money for you".
 *
 * Optional `batch` prop ({batch, totalBatches, status}) renders a small batch
 * progress strip above the step list when the upload is being chunked into
 * multiple multipart requests (large drops > ~6 files).
 */
export const ProcessingOverlay = ({ batch = null }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => (s + 1) % STEPS.length);
    }, 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="fixed inset-0 bg-[#0A0A0F]/95 backdrop-blur-sm z-50 flex items-center justify-center px-6"
      data-testid="processing-overlay"
    >
      <div className="cartiq-card max-w-md w-full relative overflow-hidden">
        <div className="accent-bar absolute top-0 left-0 right-0" />

        {/* Piggy bank animation stage */}
        <div className="relative h-40 mb-4" data-testid="piggybank-stage" aria-hidden>
          {/* Coins falling */}
          <div className="absolute inset-x-0 top-0 flex justify-center gap-6">
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="piggy-coin"
                style={{
                  animationDelay: `${i * 0.25}s`,
                  background: i === 2 ? "#FFCB11" : i % 2 ? "#FF9D3D" : "#FF6B00",
                }}
              >
                <span className="piggy-coin-face">₹</span>
              </span>
            ))}
          </div>

          {/* Piggy bank SVG */}
          <svg
            viewBox="0 0 120 80"
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-36 piggy-bounce"
            role="img"
            aria-label="CartIQ savings bank"
          >
            {/* Body */}
            <ellipse cx="60" cy="50" rx="45" ry="25" fill="#FF6B00" />
            {/* Belly shine */}
            <ellipse cx="50" cy="45" rx="20" ry="8" fill="#FF9D3D" opacity="0.4" />
            {/* Snout */}
            <ellipse cx="100" cy="50" rx="14" ry="10" fill="#E55C00" />
            <circle cx="96" cy="47" r="1.8" fill="#0A0A0F" />
            <circle cx="104" cy="47" r="1.8" fill="#0A0A0F" />
            {/* Eye */}
            <circle cx="84" cy="40" r="2.5" fill="#0A0A0F" />
            <circle cx="84.7" cy="39.3" r="0.8" fill="#fff" />
            {/* Coin slot */}
            <rect x="54" y="26" width="16" height="2.5" rx="1.2" fill="#0A0A0F" />
            {/* Ears */}
            <path d="M70 29 L78 20 L80 32 Z" fill="#E55C00" />
            {/* Legs */}
            <rect x="30" y="68" width="8" height="10" rx="2" fill="#E55C00" />
            <rect x="50" y="70" width="8" height="8" rx="2" fill="#E55C00" />
            <rect x="78" y="68" width="8" height="10" rx="2" fill="#E55C00" />
            {/* Curly tail */}
            <path d="M16 46 Q10 38 16 32 Q22 28 20 22" stroke="#E55C00" strokeWidth="3" fill="none" strokeLinecap="round" />
          </svg>

          {/* Savings label */}
          <div className="absolute bottom-0 right-2 text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold font-mono-num">
            saving…
          </div>
        </div>

        <div className="font-display uppercase text-xl text-[#F0EDE8] mb-3 text-center">
          Analysing your cart
        </div>

        {batch && batch.totalBatches > 1 && (
          <div
            className="mb-3 px-3 py-2 border border-[#FF6B00]/40 bg-[#FF6B00]/10 text-center"
            data-testid="batch-progress-strip"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
              Batch {batch.batch} of {batch.totalBatches}
            </div>
            <div className="text-xs text-[#F0EDE8]">
              {batch.status === "compressing"
                ? "Optimising screenshots before upload…"
                : batch.status === "uploading"
                ? "Sending invoices to the server…"
                : batch.status === "processing"
                ? "AI is reading this batch…"
                : "Batch ready — moving on"}
            </div>
            <div className="mt-2 h-1 bg-[#2A2A35] overflow-hidden">
              <div
                className="h-full bg-[#FF6B00] transition-[width] duration-300"
                style={{ width: `${(batch.batch / batch.totalBatches) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`flex items-center gap-2 text-sm font-mono-num transition-colors duration-500 ${
                i <= step ? "text-[#F0EDE8]" : "text-[#555566]"
              }`}
            >
              <span
                className={`w-2 h-2 inline-block ${
                  i < step
                    ? "bg-[#1D9E75]"
                    : i === step
                    ? "bg-[#FF6B00] animate-pulse"
                    : "bg-[#2A2A35]"
                }`}
              />
              {label}
              {i < step ? <span className="text-[#1D9E75] ml-auto">✓</span> : null}
            </div>
          ))}
        </div>

        <div className="mt-5 text-xs text-[#888] tracking-wider uppercase text-center">
          Typically 30–60 seconds
        </div>
      </div>
    </div>
  );
};

export default ProcessingOverlay;
